// Claim an account: choose the password it was created without.
//
// Reached from a single-use link (`/set-password?token=…`) and from nowhere
// else — there is no way to arrive here by browsing, which is why the page
// carries no email field. The token names the account; asking someone to type
// an address we already know only invites them to type a different one.
//
// THE PAGE STATES THE RULE BEFORE IT ENFORCES IT. The minimum length is on
// screen from the first render, not produced as an error after a rejected
// attempt. The same goes for the confirm field: it is checked as they type, so
// a mismatch is visible while both boxes are still in front of them.
//
// A DEAD LINK IS A DEAD END, deliberately. Expired, spent and never-valid all
// read the same and none of them offers "send me another" — that button would
// have to accept an email address from an unauthenticated stranger and tell
// them whether it exists. Re-inviting is a thing a principal does.

import { getSetupAccount, isServerBusy, setupPassword, type User } from "../data/api.js";
import { SERVER_BUSY } from "../ui/retry.js";

/** Mirrors server/setup.go minPasswordLen. The server is the enforcement; this
 *  is the courtesy of saying so before the round trip. */
const MIN_PASSWORD = 12;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function field(labelText: string, autocomplete: AutoFill): {
  wrap: HTMLLabelElement;
  input: HTMLInputElement;
} {
  const wrap = el("label", "fld");
  wrap.appendChild(el("span", "lab", labelText));
  const input = el("input");
  input.type = "password";
  input.required = true;
  input.autocomplete = autocomplete;
  wrap.appendChild(input);
  return { wrap, input };
}

/** The token this page was opened with, or "" when there is none. */
function tokenFromLocation(): string {
  return new URLSearchParams(window.location.search).get("token") ?? "";
}

export function mountSetPassword(container: HTMLElement, onSuccess: (user: User) => void): void {
  const wrap = el("div", "login-wrap");
  const card = el("form", "block login-card");
  const token = tokenFromLocation();

  card.appendChild(el("div", "eyebrow", "Set your password"));
  const heading = el("h2", undefined, "Welcome");
  card.appendChild(heading);
  const who = el("p", "note", "Checking your link…");
  card.appendChild(who);

  const password = field("New password", "new-password");
  const confirm = field("Repeat it", "new-password");
  password.input.minLength = MIN_PASSWORD;
  confirm.input.minLength = MIN_PASSWORD;
  card.append(password.wrap, confirm.wrap);

  const hint = el(
    "p",
    "note",
    `At least ${MIN_PASSWORD} characters. Nobody else knows this password, ` +
      `including us — a password manager is the right home for it.`,
  );
  card.appendChild(hint);

  const error = el("p", "note field-err");
  error.hidden = true;
  card.appendChild(error);

  const submit = el("button", "btn primary");
  submit.type = "submit";
  submit.textContent = "Set password and sign in";
  submit.style.width = "100%";
  submit.disabled = true;
  card.appendChild(submit);

  const showError = (message: string, hold = false): void => {
    error.textContent = message;
    error.classList.toggle("hold", hold);
    error.hidden = false;
  };

  /** The link itself failed. Everything on the card becomes inert: there is
   *  nothing here to retry, and leaving live fields would suggest otherwise. */
  const killCard = (message: string): void => {
    who.textContent = message;
    password.wrap.hidden = true;
    confirm.wrap.hidden = true;
    hint.hidden = true;
    submit.hidden = true;
  };

  let ready = false; // the token named an account
  let busy = false;

  /** Both boxes agree and are long enough. Checked on every keystroke so the
   *  button's state is the answer, rather than a rejection after submitting. */
  const revalidate = (): void => {
    const value = password.input.value;
    const matched = value === confirm.input.value;
    const longEnough = [...value].length >= MIN_PASSWORD;
    submit.disabled = !ready || busy || !matched || !longEnough;
    if (confirm.input.value !== "" && !matched) {
      showError("Those two do not match.");
    } else {
      error.hidden = true;
    }
  };
  password.input.addEventListener("input", revalidate);
  confirm.input.addEventListener("input", revalidate);

  // Name the account before asking for anything.
  if (token === "") {
    killCard("This link is missing its token. Ask for a new one.");
  } else {
    void getSetupAccount(token).then((res) => {
      if (!res.ok) {
        killCard(
          res.status === 404
            ? "This setup link is not valid — it may have been used already or expired. Ask for a new one."
            : "Server unavailable. Try that link again shortly.",
        );
        return;
      }
      ready = true;
      heading.textContent = `Welcome, ${res.data.name.split(" ")[0]}`;
      who.textContent = `Choose a password for ${res.data.email}.`;
      password.input.focus();
      revalidate();
    });
  }

  card.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    if (busy || !ready) return;
    error.hidden = true;
    busy = true;
    submit.disabled = true;
    submit.textContent = "Setting…";

    void setupPassword(token, password.input.value).then((res) => {
      if (res.ok) {
        // The server opened the session, so this lands already signed in.
        onSuccess(res.data);
        return;
      }
      busy = false;
      submit.textContent = "Set password and sign in";
      revalidate();
      if (isServerBusy(res)) {
        showError(SERVER_BUSY, true);
        return;
      }
      if (res.status === 404) {
        killCard("This setup link is no longer valid. Ask for a new one.");
        return;
      }
      // 400 is the length rule, and the server's wording is the authority.
      showError(res.status === 400 ? res.error : "Server unavailable.");
    });
  });

  wrap.appendChild(card);
  container.replaceChildren(wrap);
}
